import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { isEmpty } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';

import { DEFAULT_PAGE_SIZE } from '@/constants/common';
import { PRODUCTS } from '@/constants/products.constants';
import type { SearchFilters } from '@/features/SearchBar';
import useHandleError from '@/hooks/useHandleError';
import { allReportsAtom, reportsAtom } from '@/pages/Reports/Reports.store';
import { axiosClient } from '@/services/BaseApi.service';
import { userIsInternalAtom } from '@/store/user.store';

import {
  ALL_ORGANIZATIONS,
  NOOP_ORGANIZATION,
  PRODUCT_TYPES_TO_PULL
} from './Organizations.constants';
import type {
  Organization,
  OrganizationIdNames,
  OrganizationProductsDTO,
  OrganizationProductsDTOKeys,
  OrganizationsProductsType,
  ProductType
} from './Organizations.types';
import { mapOrganizationsDTO } from './Organizations.utils';

export const organizationsAtom = atom<Organization[]>([]);
export const organizationsFiltersAtom = atom<SearchFilters>({});

export const organizationIdNameAtom = atom((get) => {
  const organizations = get(organizationsAtom);
  const organizationIdNamesObject: OrganizationIdNames = {};

  organizations.forEach((org) => {
    organizationIdNamesObject[org.id] = org.name;
  });

  return organizationIdNamesObject;
});

const jotaiSessionStorage = createJSONStorage<number>(() => sessionStorage);
export const selectedOrganizationIdAtom = atomWithStorage<number>(
  'selectedOrgId',
  0, // TODO: this should be set to the users default organization
  jotaiSessionStorage
);

export const productsAtom = atom((get) => {
  const organizations = get(organizationsAtom);
  let products: ProductType[] = [];
  organizations.forEach((org) => {
    if (org.products) products = products.concat(org.products as ProductType[]);
  });
  return products;
});

export const tablesPageAtom = atom(0);
export const tablesRowsAtom = atomWithStorage<number>(
  'OrganizationTablesRows',
  DEFAULT_PAGE_SIZE
);

let prevOrganizations: OrganizationProductsDTO[] = [];
export const useFetchOrganizations = () => {
  const [, setOrganizations] = useAtom(organizationsAtom);
  const handleError = useHandleError();

  const { data, error, isError, refetch, isFetching } = useQuery<
    OrganizationProductsDTO[]
  >({
    queryKey: [
      `organizations/products/?product_types=${PRODUCT_TYPES_TO_PULL}`
    ],
    staleTime: 60 * 10 * 1000 // 10 minutes
  });

  // TODO: this effect is run too many times and updates the organizations even if data is not fetched
  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch organization products.'
      );
    }
    if (data && data !== prevOrganizations) {
      setOrganizations(
        mapOrganizationsDTO(data as OrganizationsProductsType[])
      );
      prevOrganizations = data;
    }
  }, [isError, data]);

  return {
    data,
    refetch,
    isFetching,
    error
  };
};

export const useSyncOrganization = () => {
  const handleError = useHandleError();
  const sync = useCallback(
    (id: number) =>
      axiosClient
        .get(`/organizations/${id}/sync`)
        .then(() => {
          enqueueSnackbar('Organization Sync Successful', {
            variant: 'success'
          });
        })
        .catch((error) => handleError(error, 'Failure to sync organization.')),
    []
  );

  return sync;
};

export const filteredOrganizationsAtom = atom((get) => {
  const organizations = get(organizationsAtom);
  const filters = get(organizationsFiltersAtom);

  if (isEmpty(filters)) return organizations;

  return organizations.filter((organization) =>
    Object.keys(filters).every((k) =>
      filters[k].some((f) => {
        // use JSON.stringify to search for product values
        if (k === 'any') {
          return JSON.stringify(organization)
            .toLocaleLowerCase()
            .includes(f.toLocaleLowerCase());
        }

        // product_type is an array
        if (k === 'product_type') {
          return organization.product_type.some((pt) => pt.includes(f));
        }

        if (k === 'product_sub_type') {
          return organization.product_sub_type.includes(f);
        }

        if (k === 'is_restricted') {
          return organization.is_restricted === (f === 'true');
        }

        return organization[k as OrganizationProductsDTOKeys] === f;
      })
    )
  );
});

export const activeOrganizationsAtom = atom((get) => {
  // Filter out organizations that have no active products, does NOT include grace period
  const organizations = get(organizationsAtom);
  const today = new Date();
  return organizations.filter((org) =>
    org.products?.some(
      (product) => product.active && new Date(product.end_date) >= today
    )
  );
});

export const selectedOrganizationAtom = atom<Organization>((get) => {
  const selectedOrganizationId = get(selectedOrganizationIdAtom);
  const organizations = get(organizationsAtom);

  if (organizations.length === 0) return NOOP_ORGANIZATION;

  // When the user has access to only one organization always return that organization
  if (organizations.length === 1) return organizations[0];

  return (
    organizations.find(
      (org: Organization) => org.id === selectedOrganizationId
    ) || ALL_ORGANIZATIONS
  );
});

// atom with a boolean value that checks if the organization has EDR products
export const orgHasEdrProductsAtom = atom<boolean>((get) => {
  const selectedOrganization = get(selectedOrganizationAtom);
  return selectedOrganization.product_type.some(
    (product) => product === PRODUCTS.EDR
  );
});

// atom with a boolean value that checks if the organization has EDR reports available
export const orgHasEdrReportsAtom = atom<boolean>((get) => {
  const reports = get(reportsAtom);
  const allReports = get(allReportsAtom);
  const userIsInternal = get(userIsInternalAtom);
  const selectedOrganization = get(selectedOrganizationAtom);

  const selectedReports =
    userIsInternal && selectedOrganization.label === 'all'
      ? allReports
      : reports;

  return selectedReports.some((report) => report.report_type_id === 4);
});

// atom with a boolean value that checks if the organization has VMP products
export const orgHasVmpProductsAtom = atom<boolean>((get) => {
  const selectedOrganization = get(selectedOrganizationAtom);
  return selectedOrganization.product_type.some(
    (product) => product === PRODUCTS.VMP || product === PRODUCTS.VMPE
  );
});

// atom with a boolean value that checks if the organization has VMP reports available
export const orgHasVmpReportsAtom = atom<boolean>((get) => {
  const reports = get(reportsAtom);
  const allReports = get(allReportsAtom);
  const userIsInternal = get(userIsInternalAtom);
  const selectedOrganization = get(selectedOrganizationAtom);

  const selectedReports =
    userIsInternal && selectedOrganization.label === 'all'
      ? allReports
      : reports;

  return selectedReports.some((report) => report.report_type_id === 3);
});

export const orgProductsAtom = atom((get) => {
  const products = get(productsAtom);
  const selectedOrganization = get(selectedOrganizationAtom);

  const productsWithAutoSoc = [
    ...products,
    {
      code: PRODUCTS.LOG_KANATI,
      product_type: PRODUCTS.LOG_KANATI,
      organization_id: selectedOrganization.id,
      active: !!selectedOrganization.is_auto_soc
    } as ProductType
  ];

  return productsWithAutoSoc.filter(
    (product) =>
      product.organization_id === selectedOrganization.id && product.active
  );
});

export const useFetchCleanUpItems = (
  organizationId: number
): {
  data: string[] | undefined;
  refetch: () => void;
  isFetching: boolean;
  isSuccess: boolean;
} => {
  const handleError = useHandleError();
  const { data, refetch, isFetching, isSuccess, error, isError } = useQuery<
    string[] | undefined
  >({
    queryKey: [`organizations/${organizationId}/items_to_clean_up`],
    enabled: !!organizationId,
    staleTime: 1000
  });
  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch items to clean up.');
    }
  }, [isError, error]);

  return { data, refetch, isFetching, isSuccess };
};
